import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { Button, Input, Badge, Card, DataTable } from '@clinic-os/ui';
import type { Column } from '@clinic-os/ui';
import type { Patient } from '@clinic-os/shared/types';
import { usePatients, useCreatePatient } from '../../hooks/usePatients';
import { AddPatientDialog } from './AddPatientDialog';

export function PatientsPage() {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const { data: patients = [], isLoading } = usePatients();
  const createPatient = useCreatePatient();

  const filtered = patients.filter(
    (p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase())
  );

  const columns: Column<Patient>[] = [
    {
      key: 'name',
      header: 'Patient',
      render: (p) => (
        <Link
          to={`/patients/${p.id}`}
          className="flex items-center gap-3 hover:text-blue-600 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-medium flex-shrink-0">
            {p.firstName[0]}{p.lastName[0]}
          </div>
          <div>
            <p className="font-medium text-slate-900">{p.firstName} {p.lastName}</p>
            <p className="text-xs text-slate-500">{p.email}</p>
          </div>
        </Link>
      ),
    },
    { key: 'phone', header: 'Phone', render: (p) => <span className="text-slate-600">{p.phone ?? '—'}</span> },
    {
      key: 'allergies',
      header: 'Allergies',
      render: (p) =>
        p.allergies.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {p.allergies.slice(0, 2).map((a) => (
              <Badge key={a} variant="error">{a}</Badge>
            ))}
            {p.allergies.length > 2 && (
              <span className="text-xs text-slate-500">+{p.allergies.length - 2}</span>
            )}
          </div>
        ) : (
          <span className="text-slate-500">None</span>
        ),
    },
    {
      key: 'medications',
      header: 'Medications',
      render: (p) =>
        p.medications.length > 0 ? (
          <span className="text-sm text-slate-600">{p.medications.length} recorded</span>
        ) : (
          <span className="text-slate-500">—</span>
        ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (p) => <span className="text-slate-500 text-xs line-clamp-1">{p.notes || '—'}</span>,
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Patients</h1>
          <p className="text-sm text-slate-500 mt-1">{patients.length} patients in your clinic</p>
        </div>
        <Button size="lg" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4" />
          Add Patient
        </Button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search patients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <DataTable
        label="Patients"
        columns={columns}
        data={filtered}
        keyExtractor={(p) => p.id}
        emptyMessage={isLoading ? 'Loading patients...' : 'No patients found'}
      />

      <AddPatientDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdd={(data) => {
          createPatient.mutate(data);
          setShowAdd(false);
        }}
      />
    </div>
  );
}
